import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, Microscope, ShieldCheck, Stethoscope, Sparkles } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { HAIR_SERVICES, HAIR_MEMBERSHIPS, IMAGES } from "../mock";
import { fetchHairServices } from "../api";

const WHY = [
  { icon: Stethoscope, title: "Physician-Led", text: "Every plan is designed by a physician specialised in hair restoration." },
  { icon: Microscope, title: "Research-Proven", text: "Only scientifically validated medical and surgical treatments." },
  { icon: ShieldCheck, title: "Tailored Plans", text: "Treatments customised to your diagnosis, goals and lifestyle." },
  { icon: Sparkles, title: "Natural Results", text: "Undetectable density that looks and feels completely your own." },
];

export default function Hair() {
  const [services, setServices] = useState(HAIR_SERVICES);
  useEffect(() => {
    fetchHairServices().then((d) => { if (d && d.length) setServices(d); }).catch((e) => console.error("Hair services load failed:", e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useReveal([services]);

  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center bg-charcoal overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hairHero} alt="The Hair Clinic" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/55 to-charcoal/20" />
        </div>
        <div className="relative z-10 max-w-[1300px] mx-auto px-6 w-full">
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-rule" />
              <span className="eyebrow text-gold">The Hair Clinic at Aesthetix MD</span>
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-light text-cream leading-[1.08]">
              Medical &amp; surgical <span className="italic text-gold">hair restoration</span>
            </h1>
            <p className="font-sans text-cream/75 text-lg leading-relaxed mt-7">
              Scientifically proven treatments for hair thinning and loss — in men and women — planned and delivered by a physician specialised in the field.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/booking" className="btn-gold">Free Consultation</Link>
              <a href="#treatments" className="btn-outline-gold">View Treatments</a>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="bg-cream py-28">
        <div className="max-w-[1300px] mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-rule" />
              <span className="eyebrow text-gold">The Hair Clinic</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-light text-ink leading-tight">
              Because hair loss is <span className="italic text-gold">medical</span>
            </h2>
            <p className="font-sans text-muted-ink text-[15px] leading-[1.9] mt-7">
              Hair thinning and loss increasingly affects both men and women. So many commercial products promise a fix — but thinning is often genetic or medical in nature.
            </p>
            <p className="font-sans text-muted-ink text-[15px] leading-[1.9] mt-4">
              At The Hair Clinic we provide medical and surgical treatment options that are scientifically and research-proven. Every plan is tailored to you after a consultation with a physician specialised in the field.
            </p>
            <div className="mt-9">
              <span className="eyebrow text-muted-ink">Proudly a member of</span>
              <div className="flex flex-wrap gap-3 mt-4">
                {HAIR_MEMBERSHIPS.map((m) => (
                  <span key={m} className="px-4 py-2 border border-gold/40 text-gold text-xs tracking-[0.2em]">{m}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="relative reveal">
            <div className="img-zoom rounded-sm overflow-hidden">
              <img src={IMAGES.hairGlossy} alt="Healthy hair" className="w-full h-[560px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 w-28 h-28 border border-gold hidden md:block" />
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section id="treatments" className="bg-charcoal py-28 grain relative">
        <div className="max-w-[1300px] mx-auto px-6 relative z-10">
          <div className="text-center max-w-2xl mx-auto reveal">
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="gold-rule" />
              <span className="eyebrow text-gold">Hair Treatments</span>
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-light text-cream leading-tight">
              Solutions for every stage of <span className="italic text-gold">hair loss</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mt-16">
            {services.map((s, i) => (
              <div key={s.id} className="lux-card group bg-charcoal-soft border border-white/5 rounded-sm overflow-hidden flex flex-col sm:flex-row reveal" style={{ transitionDelay: `${(i % 2) * 90}ms` }}>
                <div className="img-zoom sm:w-2/5 h-56 sm:h-auto shrink-0">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-7 flex flex-col">
                  <span className="eyebrow text-gold">{s.category}</span>
                  <h3 className="font-display text-2xl text-cream mt-2 leading-snug">{s.name}</h3>
                  <p className="font-sans text-cream/55 text-sm leading-relaxed mt-3 flex-1">{s.description}</p>
                  <div className="flex items-center justify-between mt-5 pt-5 border-t border-white/10">
                    <div className="flex items-center gap-4 text-xs text-cream/60">
                      <span className="text-gold uppercase tracking-wide">{s.price}</span>
                      <span className="flex items-center gap-1.5"><Clock size={13} /> {s.duration}</span>
                    </div>
                    <Link to="/booking" className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-cream hover:text-gold transition-colors">
                      Book <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="bg-cream-deep py-24">
        <div className="max-w-[1300px] mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY.map((w, i) => (
            <div key={w.title} className="bg-white rounded-sm p-8 reveal" style={{ transitionDelay: `${i * 70}ms` }}>
              <w.icon size={28} strokeWidth={1.2} className="text-gold" />
              <h3 className="font-display text-xl text-ink mt-5">{w.title}</h3>
              <p className="font-sans text-muted-ink text-sm leading-relaxed mt-2">{w.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-charcoal py-20">
        <div className="max-w-2xl mx-auto px-6 text-center reveal">
          <h2 className="font-display text-3xl lg:text-4xl font-light text-cream">Ready to restore your confidence?</h2>
          <p className="font-sans text-cream/60 mt-4">Book a complimentary, physician-led hair consultation today.</p>
          <Link to="/booking" className="inline-block btn-gold mt-8">Book Consultation</Link>
        </div>
      </section>
    </main>
  );
}
